import { NgStyle } from '@angular/common'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSpinner, faX } from '@fortawesome/free-solid-svg-icons'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable } from 'rxjs'

import { FeatureType, RepositoryModal, SafetyCorrectiveAction } from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'

@Component({
    templateUrl: './safety-corrective-action.component.html',
    styleUrls: ['./safety-checklist-modal.css'],
    standalone: true,
    imports: [FormsModule, FaIconComponent, NgStyle, VoiceTextFieldComponent],
})
export class SafetyCorrectiveActionComponent extends RepositoryModal<SafetyCorrectiveAction> {
    faX = faX
    faSpinner = faSpinner

    attachments: any[] = []
    progress = 0

    get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyChecklistEdit])
    }

    removeAttachment(file: any) {
        this.attachments.remove(file)
    }

    async onFileChanged(event: Event) {
        const input = event.target as HTMLInputElement
        const files = input.files
        if (!files) return

        const progress = (p: number) => {
            this.progress = p
        }

        for (const file of Array.from(files)) {
            if (file.type.startsWith('image/')) {
                const options: Options = {
                    maxSizeMB: 2,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    onProgress: progress,
                }
                const compressedFile = await imageCompression(file, options)
                compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
                this.attachments.push(compressedFile)
            } else {
                const docFile = file as any
                docFile.url = './Resources/pdfImage.png'
                this.attachments.push(docFile)
            }
        }

        input.value = ''
    }

    isDocument(url: string): boolean {
        const lower = url.toLowerCase()
        return ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'].some(ext => lower.endsWith(ext))
    }

    getExistingFileSrc(url: string): string {
        if (this.isDocument(url)) {
            return './Resources/pdfImage.png'
        }
        return './Resources/Images/SafetyChecklist/' + url + '?w=300'
    }

    getPendingFileSrc(file: any): string {
        if (file.type === 'application/pdf' || (file.name && this.isDocument(file.name))) {
            return './Resources/pdfImage.png'
        }
        return file.url
    }

    displayFileName(index: number): string {
        return this.target.imageFileNames?.[index] ?? 'Attachment'
    }

    openExistingFile(url: string) {
        window.open('./Resources/Images/SafetyChecklist/' + url, '_blank')
    }

    private uploadAttachments(id: string): Observable<any> {
        const uploadData = new FormData()
        uploadData.append('id', id)
        for (let i = 0; i < this.attachments.length; i++) {
            uploadData.append('myFile' + i, this.attachments[i], this.attachments[i].name)
        }
        return this.services.data.post('api/SafetyCorrectiveAction/UploadImages', uploadData)
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: successResult => {
                if (this.attachments.length === 0) {
                    resolveSave(successResult)
                    return
                }

                this.uploadAttachments(successResult.id).subscribe({
                    next: () => resolveSave(successResult),
                    error: errorResult => rejectSave(errorResult),
                })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }
}
